<template>
  <button
    ref="button"
    :disabled="isDisabled"
    :data-type="type"
    :class="[
      'px-6 py-4 font-bold rounded-none text-white uppercase disabled:cursor-not-allowed transition duration-200 ease-in-out flex items-center justify-center relative',
      {busy, cooldown: localCooldown},
    ]"
    :style="{animationDuration: localCooldown ? localCooldown + 'ms' : null}"
    v-on="$listeners"
  >
    <span :class="['flex items-center', {'invisible': busy}]">
      <slot />
    </span>
    <rotating-cube v-if="busy" size="12" class="absolute inset-0 m-auto" />
  </button>
</template>

<script>
  import {computed, ref, watch} from '@vue/composition-api'

  import RotatingCube from '@/components/rotating-cube.vue'

  export default {
    name: 'McButton',

    components: {
      RotatingCube,
    },

    props: {
      type: {
        type: String,
        default: 'default',
      },

      busy: {
        type: Boolean,
        default: false,
      },

      disabled: {
        type: Boolean,
        default: false,
      },
    },

    setup (props) {
      let button = ref(null)

      let localCooldown = ref(0)
      let isDisabled = computed(() => Boolean(props.disabled || props.busy || localCooldown.value))

      watch(localCooldown, cooldown => {
        if (cooldown) {
          localCooldown.value = cooldown
          button.value.addEventListener('animationend', () => localCooldown.value = 0)
        }
      })

      function cooldown (time = 3000) {
        localCooldown.value = time
      }

      return {
        button,

        isDisabled,
        localCooldown,

        cooldown,
      }
    },
  }
</script>

<style lang="scss" scoped>
  $bg-default: #757575;
  $text-disabled: #4d4d4d;
  $bg-disabled: #bdbdbd;
  $bg-cooldown: #9d9d9d;

  button {
    box-shadow: 0 -4px rgba(0, 0, 0, 0.3) inset, 0 4px rgba(255, 255, 255, 0.3) inset, -4px 0 rgba(255, 255, 255, 0.3) inset, 4px 0 rgba(0, 0, 0, 0.3) inset, 0 4px 0 0 rgba(0, 0, 0, 0.15);
    text-shadow: 0 2px 0 rgba(0, 0, 0, 0.25);

    &:active {
      transform: scale(0.95);
    }

    &[data-type=default] {
      background-color: $bg-default;

      &:hover, &:active {
        background-color: #525252;
      }
    }

    &[data-type=primary] {
      background-color: #34aa2f;

      &:hover, &:active {
        background-color: #288024;
      }
    }

    &[data-type=caution] {
      background-color: #e6b40d;

      &:hover, &:active {
        background-color: #b0890b;
      }
    }

    &:disabled {
      color: $text-disabled;
      text-shadow: none;
      background-color: $bg-disabled;
      box-shadow: 0 -4px rgba(0, 0, 0, 0.15) inset, 0 4px rgba(255, 255, 255, 0.3) inset, -4px 0 rgba(255, 255, 255, 0.3) inset, 4px 0 rgba(0, 0, 0, 0.15) inset, 0 4px 0 0 rgba(0, 0, 0, 0.15);
      pointer-events: none;

      &:hover {
        background-color: $bg-disabled;
      }
    }

    &.busy {
      $bg-color: #313131;

      background-color: $bg-color;
      pointer-events: none;

      &:hover {
        background-color: $bg-color;
      }
    }

    &.cooldown {
      background: $bg-cooldown linear-gradient(to right, $bg-disabled 50%, $bg-cooldown 0, $bg-cooldown 100%);
      background-size: 200% 100%;
      animation: linear forwards cooldown;

      @keyframes cooldown {
        0% {background-position: 0 0;}
        100% {background-position: 100% 0;}
      }
    }
  }
</style>
